import React, { useState } from 'react';
import { Filter, X, Calendar, Trophy, Target, BarChart3 } from 'lucide-react';
import { Card } from './Card';
import { Button } from './Button';
import { Input } from './Input';
import { Select } from './Select';
import { FiltrosTusInformes } from '@/hooks/useTusInformes';
import { useAuth } from '../../contexts/AuthContext';
import { Nivel, Competicion, getCategoriaLabel, getNivelLabel, getCompeticiones, getCompeticionLabel, getCategorias } from '@/types/enums';

interface FiltrosInformesProps {
  filtros: FiltrosTusInformes;
  onFiltrosChange: (filtros: Partial<FiltrosTusInformes>) => void;
  onLimpiarFiltros: () => void;
  totalInformes: number;
  informesFiltrados: number;
}

/**
 * Componente para filtrar informes del entrenador
 */
export const FiltrosInformes: React.FC<FiltrosInformesProps> = ({
  filtros,
  onFiltrosChange,
  onLimpiarFiltros,
  totalInformes,
  informesFiltrados
}) => {
  const [mostrarFiltros, setMostrarFiltros] = useState(false);
  const { user } = useAuth();

  /**
   * Maneja el cambio de filtros
   */
  const handleFiltroChange = (campo: keyof FiltrosTusInformes, valor: any) => {
    onFiltrosChange({ [campo]: valor || undefined });
  };

  /**
   * Cuenta los filtros activos
   */
  const filtrosActivos = Object.values(filtros).filter(valor => 
    valor !== undefined && valor !== ''
  ).length;

  return (
    <Card className="mb-4">
      {/* Header de filtros */}
      <div className="flex items-center justify-between p-4 border-b border-secondary-800">
        <div className="flex items-center space-x-3">
          <Filter className="w-5 h-5 text-primary-400" />
          <div>
            <h3 className="font-semibold text-secondary-50">
              Filtros
              {filtrosActivos > 0 && (
                <span className="ml-2 px-2 py-1 bg-primary-500 text-white text-xs rounded-full">
                  {filtrosActivos}
                </span>
              )}
            </h3>
            <p className="text-sm text-secondary-400">
              {informesFiltrados} de {totalInformes} informes
            </p>
          </div>
        </div>
        
        <div className="flex items-center space-x-2">
          {filtrosActivos > 0 && (
            <Button
              onClick={onLimpiarFiltros}
              variant="secondary"
              size="sm"
              className="text-xs"
            >
              <X className="w-4 h-4 mr-1" />
              Limpiar
            </Button>
          )}
          <Button
            onClick={() => setMostrarFiltros(!mostrarFiltros)}
            variant="primary"
            size="sm"
          >
            {mostrarFiltros ? 'Ocultar' : 'Mostrar'}
          </Button>
        </div>
      </div>

      {/* Filtros expandibles */}
      {mostrarFiltros && (
        <div className="p-4 space-y-4">
          {/* Filtros de fecha */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-secondary-300 mb-2">
                <Calendar className="w-4 h-4 inline mr-1" />
                Fecha desde
              </label>
              <Input
                type="date"
                value={filtros.fechaInicio || ''}
                onChange={(e) => handleFiltroChange('fechaInicio', e.target.value)}
                className="w-full"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-secondary-300 mb-2">
                <Calendar className="w-4 h-4 inline mr-1" />
                Fecha hasta
              </label>
              <Input
                type="date"
                value={filtros.fechaFin || ''}
                onChange={(e) => handleFiltroChange('fechaFin', e.target.value)}
                className="w-full"
              />
            </div>
          </div>

          {/* Filtro de competición */}
          <div>
            <label className="block text-sm font-medium text-secondary-300 mb-2">
              <Trophy className="w-4 h-4 inline mr-1" />
              Competición
            </label>
            <Select
              value={filtros.competicion || ''}
              onChange={(e) => handleFiltroChange('competicion', e.target.value as Competicion || undefined)}
              options={[
                { value: '', label: 'Todas las competiciones' },
                ...getCompeticiones(user?.seccion).map(competicion => ({
                  value: competicion,
                  label: getCompeticionLabel(competicion)
                }))
              ]}
              className="w-full"
            />
          </div>

          {/* Filtros de categoría y nivel */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-secondary-300 mb-2">
                <Target className="w-4 h-4 inline mr-1" />
                Categoría
              </label>
              <Select
                value={filtros.categoria || ''}
                onChange={(e) => handleFiltroChange('categoria', e.target.value)}
                className="w-full"
                options={[
                  { value: '', label: 'Todas las categorías' },
                  ...getCategorias(user?.seccion).map((categoria) => ({
                    value: categoria,
                    label: getCategoriaLabel(categoria)
                  }))
                ]}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-secondary-300 mb-2">
                <BarChart3 className="w-4 h-4 inline mr-1" />
                Nivel
              </label>
              <Select
                value={filtros.nivel || ''}
                onChange={(e) => handleFiltroChange('nivel', e.target.value)}
                className="w-full"
                options={[
                  { value: '', label: 'Todos los niveles' },
                  ...Object.values(Nivel).map((nivel) => ({
                    value: nivel,
                    label: getNivelLabel(nivel)
                  }))
                ]}
              />
            </div>
          </div>
        </div>
      )}
    </Card>
  );
};